// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
/* eslint-disable-next-line no-unused-vars */
import React, { type Node } from 'react';

import {
  type ColorName,
  Colors,
  type ContentSectioningElement,
  type InlineTextElement,
  type TextContentElement,
  Typography,
  type TypographyName,
} from '@zapier/design-system';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

/** @jsx isolateComponent */

type TextElement =
  | ContentSectioningElement
  | TextContentElement
  | InlineTextElement;

type Props = {
  /** The color of text to render. */
  color: ColorName,
  /**
   * The type (effectively the "style") of text to render. One of a list of
   * preset types, which correspond exactly to the Zapier Design System.
   */
  type: TypographyName,
  /**
   * Text to be rendered. Accepts a Node to support minor variations, e.g., a
   * link within a heading, or an `<em>` tag for semantic emphasis.
   */
  children: Node,
  /**
   * Weight override for `type`. There are a finite number of `type`s
   * but there are weight variations for each which can be handled here
   * rather than increasing the overall number of `type`s.
   */
  fontWeight?: 400 | 600 | 700 | 800,
  /** Applies a `margin` to the text. */
  margin?: string,
  /** The HTML tag that the component will render. */
  tag: TextElement,
  /**
   * Alignment of the text. Values are intended to match CSS `text-align`
   * values. Intentionally omitting many options until use cases arise, for
   * simplicity. One of: 'inherit', 'left', 'center', 'right'.
   */
  textAlign: 'inherit' | 'left' | 'center' | 'right',
};

const Styles = labelStyles(__filename, {
  root: (props: Props) => [
    withModifier(props.type, Typography[props.type]),
    withModifier(props.color, { color: Colors[props.color] }),
    props.fontWeight &&
      withModifier(`fontWeight-${props.fontWeight}`, {
        fontWeight: props.fontWeight,
      }),
    {
      transition: 'color 0.2s ease-in-out',
      margin: props.margin,
      maxWidth: '100%',
      textAlign: props.textAlign,
    },
  ],
});

/**
 * This component precisely implements the Zapier Design System
 * for text.
 *
 * If you feel the need to add a variation on its styles, your design is likely
 * violating Zapier's style guide.
 *
 * Tags: core-ui
 */
const Text = (props: Props) => {
  return <props.tag css={Styles.root(props)}>{props.children}</props.tag>;
};

Text.defaultProps = {
  color: 'black',
  tag: 'span',
  textAlign: 'inherit',
  type: 'bodyText800',
};

export default Text;
